/**
 * Tip suggestion with optional fallback minimum, applied uniformly to a group.
 *
 * Per-option rule (legacy): if (calculated tip <= fallback) → show fallback,
 * else show the percentage-based calculated tip.
 *
 * Group rule (new): when the full tip-options array (e.g. aggregatedTipOptions.kitchenTip
 * or .driverTip) is provided, the decision is made once for the whole group:
 *   - If ANY option in the group triggers the fallback condition
 *     (calculated <= fallback for that option), then EVERY option that has a
 *     fallback returns its fallback, so the group displays consistently
 *     (e.g. $1 / $2 / $3 instead of mixed $2.25 / $2.25 / $3).
 *   - Otherwise every option returns its percentage-based calculated tip.
 *
 * If allTipOptions is omitted, the function falls back to the per-option rule
 * so existing call sites that don't pass the array keep their previous behaviour.
 *
 * Special case: when value === 0 (explicit "No Tip" option) always returns 0,
 * even if a fallback/fallbackAmount is present, because a zero-percent
 * tip should never silently become a non-zero amount.
 *
 * Supports API shape: fallbackAmount (and fallback), value as percentage
 * (e.g. 20) or decimal (e.g. 0.15).
 *
 * @param {number} subTotal - Order subtotal
 * @param {{ value: number, fallback?: number, fallbackAmount?: number }} tipOption - The option to compute for
 * @param {Array<{ value: number, fallback?: number, fallbackAmount?: number }>} [allTipOptions] - Full group of options
 * @returns {number} Suggested tip amount in dollars
 */
export function getSuggestedTipAmount(subTotal, tipOption, allTipOptions) {
  if (subTotal == null || !tipOption) return 0;
  const v = Number(tipOption.value || 0);

  if (v === 0) return 0;

  const calculated = computeCalculated(subTotal, v);
  const fallback = getFallback(tipOption);

  // Group-level decision: if the array is provided, use fallback for every
  // option whenever ANY non-zero option in the group hits the fallback floor.
  // Otherwise, fall back to the per-option legacy rule.
  const shouldUseFallback = Array.isArray(allTipOptions)
    ? allTipOptions.some((opt) => {
        const ov = Number(opt?.value || 0);
        if (ov === 0) return false;
        const oFallback = getFallback(opt);
        if (oFallback == null) return false;
        return computeCalculated(subTotal, ov) <= oFallback;
      })
    : fallback != null && calculated <= fallback;

  if (shouldUseFallback && fallback != null) return fallback;
  return calculated;
}

function computeCalculated(subTotal, v) {
  // value can be a whole-number percentage (e.g. 18 = 18 %) or
  // a decimal fraction (e.g. 0.18 = 18 %)
  return v > 0 && v <= 1
    ? Number(subTotal) * v
    : (Number(subTotal) * v) / 100;
}

function getFallback(tipOption) {
  if (tipOption?.fallback != null) return Number(tipOption.fallback);
  if (tipOption?.fallbackAmount != null) return Number(tipOption.fallbackAmount);
  return null;
}
